import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import {
  getAccountSecrets, putAccountSecrets, getAccountEvidence, type PublishAccount,
} from '@/api/playPublish';

interface Props {
  account: PublishAccount | null;
  onClose: () => void;
}

const inputCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm';

const empty = {
  google_email: '', iarc_email: '', contact_email: '', recovery_email: '', proxy: '', cookies: '', platform: '',
  password: '', totp_seed: '', backup_codes: '', daily_publish_cap: '2',
};

// The last screenshot the runner took of this console. Loaded on demand: it
// shows a signed-in Google account, so it is fetched through the worker rather
// than linked from the bucket.
function SessionShot({ accountId }: { accountId: number }) {
  const [src, setSrc] = useState('');
  const query = useQuery({
    queryKey: ['publish-account-shot', accountId],
    queryFn: () => getAccountEvidence(accountId),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (!query.data) return;
    const next = URL.createObjectURL(query.data);
    setSrc(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);
  if (query.isError) return <p className="text-xs text-red-400">{(query.error as Error).message}</p>;
  if (!src) return <div className="h-24 animate-pulse bg-surface-muted rounded" />;
  return <img src={src} alt="console session" className="max-h-64 rounded border border-border" />;
}

export function PublishAccountEditor({ account, onClose }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState<string | null>(null);
  const [showShot, setShowShot] = useState(false);

  const { data: current } = useQuery({
    queryKey: ['publish-account-secrets', account?.id],
    queryFn: () => getAccountSecrets(account!.id),
    enabled: !!account,
  });

  useEffect(() => {
    if (!account) return;
    setError(null);
    setShowShot(false);
    // Secrets are write-only: the fields start empty and what is already stored
    // is described next to them, never loaded into an input.
    setForm({
      ...empty,
      google_email: account.google_email ?? '',
      iarc_email: account.iarc_email ?? '',
      contact_email: account.contact_email ?? '',
      recovery_email: account.recovery_email ?? '',
      daily_publish_cap: String(account.daily_publish_cap ?? 2),
      platform: account.platform ?? '',
    });
  }, [account]);

  // A shared console signs in with its browser profile: only what is about the
  // console itself is edited here.
  const shared = !!account?.browser_profile_id;

  const saveMut = useMutation({
    // Blank secret fields are omitted, not sent empty: the server merges what it
    // receives, so omitting is what "keep the stored value" means.
    mutationFn: () => shared ? putAccountSecrets(account!.id, {
      iarc_email: form.iarc_email.trim(),
      contact_email: form.contact_email.trim(),
      daily_publish_cap: Number(form.daily_publish_cap) || 0,
    }) : putAccountSecrets(account!.id, {
      google_email: form.google_email.trim(),
      iarc_email: form.iarc_email.trim(),
      contact_email: form.contact_email.trim(),
      daily_publish_cap: Number(form.daily_publish_cap) || 0,
      ...(form.recovery_email.trim() ? { recovery_email: form.recovery_email.trim() } : {}),
      ...(form.proxy.trim() ? { proxy: form.proxy.trim() } : {}),
      ...(form.cookies.trim() ? { cookies: form.cookies.trim() } : {}),
      platform: form.platform,
      ...(form.password ? { password: form.password } : {}),
      ...(form.totp_seed.trim() ? { totp_seed: form.totp_seed.replace(/\s+/g, '') } : {}),
      ...(form.backup_codes.trim()
        ? { backup_codes: form.backup_codes.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean) }
        : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['publish-accounts'] });
      qc.invalidateQueries({ queryKey: ['publish-account-secrets', account?.id] });
      onClose();
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to save credentials'),
  });

  const stored = current ?? account;
  const codesLeft = stored?.backup_codes_left ?? 0;

  return (
    <Modal isOpen={!!account} onClose={onClose} title={account ? `Console access — ${account.name}` : ''} size="2xl">
      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">
          {error}
        </div>
      )}

      {shared ? (
        <p className="text-sm text-content-muted mb-4">
          A shared console: developer account <span className="font-mono">{account?.developer_id}</span> belongs to
          somebody else, who invited the Google account of browser profile “{account?.browser_profile_name}”. That
          profile holds the login — password, 2FA, proxy and cookies are edited in Browser Profiles.
        </p>
      ) : (
      <p className="text-sm text-content-muted mb-4">
        The browser half of this account: what a runner needs to survive a “verify it’s you” prompt
        without a human. Stored encrypted under its own key and never readable from here again —
        leaving a secret field empty <b>clears</b> it, so re-enter both if you change one. The
        browser profile is created by the runner once a proxy is saved; the first sign-in and every
        session refresh after it run through the stored password and TOTP.
      </p>
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        {!shared && (
        <div>
          <label className="block text-sm text-content-muted mb-1">Google account email</label>
          <input className={inputCls} value={form.google_email} onChange={(e) => setForm({ ...form, google_email: e.target.value })} placeholder="console@example.com" />
        </div>
        )}
        <div>
          <label className="block text-sm text-content-muted mb-1">IARC correspondence email</label>
          <input className={inputCls} value={form.iarc_email} onChange={(e) => setForm({ ...form, iarc_email: e.target.value })} placeholder="ratings@example.com" />
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-sm text-content-muted mb-1">Public contact email</label>
        <input className={inputCls} type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="support@example.com" />
        <p className="text-xs text-content-muted mt-1">Required for publication. Shown publicly on the store listing for apps published by this account.</p>
        {!form.contact_email.trim() && <p className="text-xs text-amber-400 mt-1">Set a contact email before publishing an app.</p>}
      </div>

      {shared ? (
        <div className="mb-4 max-w-xs">
          <label className="block text-sm text-content-muted mb-1">Publications per day</label>
          <input className={inputCls} type="number" min={0} value={form.daily_publish_cap} onChange={(e) => setForm({ ...form, daily_publish_cap: e.target.value })} />
          <p className="text-xs text-content-muted mt-1">0 disables the throttle entirely.</p>
        </div>
      ) : (<>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">
            Proxy {stored?.proxy_label && <span className="text-content-muted">· stored {stored.proxy_label}</span>}
          </label>
          <input className={inputCls} type="password" autoComplete="off" value={form.proxy} onChange={(e) => setForm({ ...form, proxy: e.target.value })} placeholder="socks5://user:pass@host:port" />
          <p className="text-xs text-content-muted mt-1">
            {account?.shardx_profile_id
              ? <>Profile <span className="font-mono">{account.shardx_profile_id}</span> is bound to this proxy for life.</>
              : 'Sticky residential, one per account. The runner creates the profile on it within a minute of saving.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">Fingerprint OS</label>
          <select
            className={inputCls}
            value={form.platform}
            disabled={!!account?.shardx_profile_id}
            onChange={(e) => setForm({ ...form, platform: e.target.value })}
          >
            <option value="">Windows (default)</option>
            <option value="windows">Windows</option>
            <option value="macos">macOS</option>
          </select>
          <p className="text-xs text-content-muted mt-1">
            {account?.shardx_profile_id
              ? 'Fixed once the profile exists.'
              : 'Match the browser this account lived in — set macOS if you are importing a session from a Mac anti-detect profile.'}
          </p>
        </div>
        <div>
          <label className="block text-sm text-content-muted mb-1">Publications per day</label>
          <input className={inputCls} type="number" min={0} value={form.daily_publish_cap} onChange={(e) => setForm({ ...form, daily_publish_cap: e.target.value })} />
          <p className="text-xs text-content-muted mt-1">0 disables the throttle entirely.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">
            Password {stored?.has_password && <span className="text-content-muted">· stored</span>}
          </label>
          <input className={inputCls} type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm text-content-muted mb-1">
            TOTP seed {stored?.has_totp && <span className="text-content-muted">· stored</span>}
          </label>
          <input className={inputCls} type="password" autoComplete="off" value={form.totp_seed} onChange={(e) => setForm({ ...form, totp_seed: e.target.value })} placeholder="base32 secret" />
        </div>
      </div>

      <label className="block text-sm text-content-muted mb-1">
        Backup codes {codesLeft > 0 && <span className={codesLeft <= 3 ? 'text-amber-400' : 'text-content-muted'}>· {codesLeft} left</span>}
      </label>
      <textarea
        className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm h-24 mb-1"
        value={form.backup_codes}
        onChange={(e) => setForm({ ...form, backup_codes: e.target.value })}
        placeholder="One per line"
      />
      <p className="text-xs text-content-muted mb-3">
        Google Prompt and passkey accounts cannot be driven at all — TOTP plus saved backup codes is
        the only second factor that works here.
      </p>

      <label className="block text-sm text-content-muted mb-1">Recovery email</label>
      <input className={`${inputCls} mb-4`} value={form.recovery_email} onChange={(e) => setForm({ ...form, recovery_email: e.target.value })} />

      <label className="block text-sm text-content-muted mb-1">
        Cookies <span className="text-content-muted">· optional</span>
        {stored?.cookies_pending && <span className="text-amber-400"> · waiting for the runner to import</span>}
      </label>
      <div className="flex items-start gap-2 mb-1">
        <textarea
          className="flex-1 px-3 py-2 rounded-md bg-background border border-border text-content font-mono text-xs h-20"
          value={form.cookies}
          onChange={(e) => setForm({ ...form, cookies: e.target.value })}
          placeholder='Paste a cookie export — JSON array (Cookie-Editor, EditThisCookie) or Netscape cookies.txt'
        />
        <label className="shrink-0 text-xs text-sky-400 hover:underline cursor-pointer pt-2">
          from file
          <input
            type="file"
            accept=".json,.txt,application/json,text/plain"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setForm({ ...form, cookies: await f.text() });
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <p className="text-xs text-content-muted mb-4">
        Loaded into the profile on the runner’s next visit and wiped here afterwards. If the session
        is alive, no sign-in happens at all. Cookies from a browser with a different fingerprint are
        exactly the identity change that gets accounts flagged — fine for a disposable account, a
        gamble for a live one.
      </p>
      </>)}

      {account?.has_session_evidence && (
        <div className="mb-4">
          {showShot
            ? <SessionShot accountId={account.id} />
            : <button type="button" className="text-xs text-sky-400 hover:underline" onClick={() => setShowShot(true)}>
                Last console screenshot
              </button>}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={saveMut.isPending} onClick={() => { setError(null); saveMut.mutate(); }}>Save</Button>
      </div>
    </Modal>
  );
}
